import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import MapIcon from "@mui/icons-material/Map";
import { DistancesTool } from "./tools/DistancesTool";
import { ComparatorTool } from "./tools/ComparatorTool";
import { WaterBillsTool } from "./tools/WaterBillsTool";
import { WaterBillClientsSettingsPage } from "../pages/WaterBillClientsSettingsPage";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { DistancesDatabasePage } from "../pages/DistancesDatabasePage";
import { PortMappingsSettingsPage } from "../pages/PortMappingsSettingsPage";
import { useAuth } from "../contexts/AuthContext";

export interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface ToolModalProps {
  open: boolean;
  onClose: () => void;
  tool: ToolDefinition | null;
}

type DistancesSubTab = "process" | "database" | "settings";

export const ToolModal: React.FC<ToolModalProps> = ({ open, onClose, tool }) => {
  const { user } = useAuth();
  const isDbAdmin = user?.role === "admin" || user?.role === "mantainer";
  const [distancesTab, setDistancesTab] = React.useState<DistancesSubTab>("process");
  // Monta "Database tratte"/"Mappature porti" solo alla prima visita (evita
  // fetch inutili quando si apre il tool solo per elaborare un file), ma poi
  // li tiene montati (visibilità a switch) per non perdere filtri/paginazione
  // saltando da una tab all'altra.
  const [visited, setVisited] = React.useState<Record<DistancesSubTab, boolean>>({
    process: true,
    database: false,
    settings: false,
  });

  // Torna sempre alla tab "Elabora file" quando si riapre il tool da zero.
  React.useEffect(() => {
    if (open) {
      setDistancesTab("process");
      setVisited({ process: true, database: false, settings: false });
    }
  }, [open]);

  const handleTabChange = (_: React.SyntheticEvent, value: DistancesSubTab) => {
    setDistancesTab(value);
    setVisited((v) => (v[value] ? v : { ...v, [value]: true }));
  };

  if (!tool) return null;

  const isDistanceTool = tool.id === "stime-distanze";
  const isWaterBillsTool = tool.id === "estrazione-bollette-acqua";
  // Tool con sotto-navigazione a tab dentro il modal (Distanze, Bollette Acqua)
  const hasSubTabs = isDistanceTool || (isWaterBillsTool && isDbAdmin);
  const subTabFullArea = hasSubTabs && distancesTab !== "process";

  const preventBrowserFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      onDragOver={preventBrowserFileDrop}
      onDrop={preventBrowserFileDrop}
      PaperProps={{
        sx: {
          width: "92vw",
          height: "90vh",
          maxWidth: "none",
          borderRadius: "1.5rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: tool.color + "18",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tool.color,
            flexShrink: 0,
            "& svg": { fontSize: "1.5rem" },
          }}
        >
          {tool.icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {tool.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {tool.description}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Sotto-navigazione specifica dello strumento Distanze: la vista del
          database tratte e le mappature porti sono utili solo qui, quindi
          vivono come tab dentro il modal invece che nella sidebar globale. */}
      {hasSubTabs && (
        <Tabs
          value={distancesTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            minHeight: 42,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            bgcolor: "background.paper",
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontSize: "0.85rem",
              fontWeight: 500,
              gap: 0.75,
            },
            "& .Mui-selected": { color: `${tool.color} !important` },
            "& .MuiTabs-indicator": { bgcolor: tool.color },
          }}
        >
          <Tab
            value="process"
            label="Elabora file"
            icon={<UploadFileIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
          />
          {isDistanceTool && isDbAdmin && (
            <Tab
              value="database"
              label="Database tratte"
              icon={<AltRouteIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
            />
          )}
          {isDistanceTool && isDbAdmin && (
            <Tab
              value="settings"
              label="Mappature porti"
              icon={<MapIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
            />
          )}
          {isWaterBillsTool && isDbAdmin && (
            <Tab
              value="settings"
              label="Settings clienti"
              icon={<PeopleOutlineIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
            />
          )}
        </Tabs>
      )}

      {/* Content area */}
      <DialogContent
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: subTabFullArea ? "stretch" : "center",
          // i contenuti lunghi (Bollette Acqua) partono dall'alto: centrati verticalmente verrebbero tagliati
          justifyContent: subTabFullArea || isWaterBillsTool ? "flex-start" : "center",
          flexDirection: "column",
          bgcolor: "#F7F8FA",
          p: subTabFullArea ? 0 : 4,
          overflowY: subTabFullArea ? "hidden" : "auto",
        }}
      >
        {isDistanceTool && (
          <>
            {/* Montati tutti insieme (visibilità a switch) così l'elaborazione
                in corso nella tab "Elabora file" non viene persa passando
                alle altre tab, ed evitiamo un refetch dei dati ogni volta. */}
            <Box sx={{ display: distancesTab === "process" ? "flex" : "none", width: "100%", justifyContent: "center" }}>
              <DistancesTool />
            </Box>
            {isDbAdmin && visited.database && (
              <Box sx={{ display: distancesTab === "database" ? "block" : "none", width: "100%", height: "100%" }}>
                <DistancesDatabasePage />
              </Box>
            )}
            {isDbAdmin && visited.settings && (
              <Box sx={{ display: distancesTab === "settings" ? "block" : "none", width: "100%", height: "100%" }}>
                <PortMappingsSettingsPage />
              </Box>
            )}
          </>
        )}
        {isWaterBillsTool && (
          <>
            {/* Montati insieme (visibilità a switch): l'area di caricamento non perde lo stato passando alle impostazioni. */}
            <Box
              sx={{
                display: distancesTab === "process" ? "flex" : "none",
                width: "100%",
                justifyContent: "center",
              }}
            >
              <WaterBillsTool />
            </Box>
            {isDbAdmin && visited.settings && (
              <Box sx={{ display: distancesTab === "settings" ? "block" : "none", width: "100%", height: "100%" }}>
                <WaterBillClientsSettingsPage />
              </Box>
            )}
          </>
        )}
        {tool.id === "controllo-report-pdf" && <ComparatorTool />}
        {tool.id !== "stime-distanze" && tool.id !== "controllo-report-pdf" && !isWaterBillsTool && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: "24px",
                bgcolor: tool.color + "15",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: tool.color,
                mb: 1,
                "& svg": { fontSize: "2.8rem" },
              }}
            >
              {tool.icon}
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary", textAlign: "center" }}
            >
              {tool.name}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center", maxWidth: 420, lineHeight: 1.7 }}
            >
              Questo strumento è attualmente in sviluppo.
              <br />
              Sarà disponibile nella prossima versione.
            </Typography>
            <Box
              sx={{
                mt: 2,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                bgcolor: tool.color + "12",
                border: "1px solid",
                borderColor: tool.color + "30",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <BuildIcon sx={{ fontSize: "1rem", color: tool.color }} />
              <Typography
                variant="caption"
                sx={{ color: tool.color, fontWeight: 500, fontSize: "0.8rem" }}
              >
                In sviluppo
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
